import { useEffect, useMemo, useState } from "react"
import { Box } from "lucide-react"
import { CatalogPickerDialog, catalogPickerCardClassName } from "@/components/ui/catalog-picker-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { fetchLibraryScene3ds, type LibraryScene3dItem } from "@/lib/library-api"

type Scene3dPickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  /** Shot location leads the sections. Null puts unlocated last and the rest by name. */
  shotLocationId?: number | null
  nested?: boolean
  onSelect: (scene: LibraryScene3dItem) => void
}

const LIST_LIMIT = 120

function matches(scene: LibraryScene3dItem, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return scene.name.toLowerCase().includes(q)
}

export function Scene3dPickerDialog({
  open,
  onOpenChange,
  projectId,
  shotLocationId = null,
  nested = false,
  onSelect,
}: Scene3dPickerDialogProps) {
  const { t } = useTranslation(["shot"])
  const [filterText, setFilterText] = useState("")
  const [scenes, setScenes] = useState<LibraryScene3dItem[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setListError(null)
    setListLoading(true)
    void fetchLibraryScene3ds(projectId, { limit: LIST_LIMIT })
      .then((page) => {
        if (cancelled) return
        setScenes(page.scene3ds)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setListError(e instanceof Error ? e.message : String(e))
        setScenes([])
      })
      .finally(() => {
        if (!cancelled) setListLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId])

  const sections = useMemo(() => {
    const filtered = scenes.filter((scene) => matches(scene, filterText))
    const byLocation = new Map<number, LibraryScene3dItem[]>()
    const unlocated: LibraryScene3dItem[] = []
    for (const scene of filtered) {
      if (scene.location_id == null) {
        unlocated.push(scene)
        continue
      }
      const list = byLocation.get(scene.location_id) ?? []
      list.push(scene)
      byLocation.set(scene.location_id, list)
    }

    const locationIds = [...byLocation.keys()]
    locationIds.sort((a, b) => {
      if (shotLocationId != null) {
        if (a === shotLocationId) return -1
        if (b === shotLocationId) return 1
      }
      const nameA = byLocation.get(a)?.[0]?.location_name ?? ""
      const nameB = byLocation.get(b)?.[0]?.location_name ?? ""
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
    })

    const card = (scene: LibraryScene3dItem) => {
      const cover = scene.thumb_url ?? scene.poster_url
      return (
        <div key={scene.id} className={catalogPickerCardClassName(false)}>
          <button
            type="button"
            onClick={() => onSelect(scene)}
            className="flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-ui-surface-sunken text-ui-text-muted"
          >
            {cover ? (
              <img src={cover} alt="" className="h-full w-full object-cover" />
            ) : (
              <Box className="h-5 w-5 opacity-60" aria-hidden />
            )}
          </button>
          <button type="button" onClick={() => onSelect(scene)} className="mt-2 w-full cursor-pointer text-left">
            <p className="line-clamp-2 text-xs font-medium text-ui-text-primary">{scene.name}</p>
          </button>
        </div>
      )
    }

    const result: { id: string; label: string; items: ReturnType<typeof card>[] }[] = []
    for (const locationId of locationIds) {
      const items = byLocation.get(locationId) ?? []
      if (items.length === 0) continue
      result.push({
        id: `loc-${locationId}`,
        label: items[0]?.location_name?.trim() || `#${locationId}`,
        items: items.map(card),
      })
    }
    if (unlocated.length > 0) {
      result.push({
        id: "unlocated",
        label: t("previz.pickerUnlocated"),
        items: unlocated.map(card),
      })
    }
    return result
  }, [filterText, onSelect, scenes, shotLocationId, t])

  const emptyLabel = listLoading
    ? t("previz.pickerLoading")
    : listError
      ? listError
      : scenes.length === 0
        ? t("previz.pickerEmpty")
        : t("previz.pickerNoResults")

  return (
    <CatalogPickerDialog
      open={open}
      onOpenChange={(next) => {
        if (next) setFilterText("")
        onOpenChange(next)
      }}
      title={t("previz.pickerTitle")}
      closeLabel={t("previz.pickerClose")}
      searchValue={filterText}
      onSearchChange={setFilterText}
      searchPlaceholder={t("previz.pickerSearch")}
      sections={sections}
      emptyLabel={emptyLabel}
      isEmpty={listError != null || (listLoading && scenes.length === 0) || (!listLoading && sections.length === 0)}
      nested={nested}
    />
  )
}

export function pickRecentScenes(
  scenes: readonly LibraryScene3dItem[],
  locationId: number | null,
  limit = 4,
): LibraryScene3dItem[] {
  const located = locationId != null ? scenes.filter((scene) => scene.location_id === locationId) : []
  const rest = scenes.filter((scene) => !located.some((item) => item.id === scene.id))
  return [...located, ...rest].slice(0, limit)
}
